/**
 * Scan a pile of barcodes, then search for all of them at once.
 *
 * "give me a batch entry icon next to the search, this will pop up an entry
 *  screen so i can scan as many as i want then search"
 *
 * The search box has always taken several terms separated by semicolons —
 * "12; 13; 15" is a morning's accessions on one screen. Typing that by hand is
 * fine for three and absurd for forty, and a wedge scanner cannot type it at
 * all: it sends the barcode followed by Enter, which submits the box and loses
 * everything scanned before it.
 *
 * So this is a textarea, and ENTER MAKES A NEW LINE. It never submits. That is
 * the whole trick — the scanner's trailing Enter becomes the separator instead
 * of ending the session. See [[project-collection-capture]] for the same
 * lesson learned on the licence scanner.
 *
 * On apply it joins the lines with the separator the lists already agree on
 * (app/utils/multi_search.py owns that rule) and hands it to the search box,
 * so nothing downstream needs to know this screen exists.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, X, Search, Trash2 } from 'lucide-react';

/** Mirrors MAX_TERMS in app/utils/multi_search.py. */
const MAX_TERMS = 100;
const SEPARATOR = '; ';

/** Lines → clean, de-duplicated terms, in the order they were scanned. */
export function parseBatch(raw) {
  const seen = new Set();
  const out = [];
  for (const piece of String(raw || '').split(/[\r\n;,\t]+/)) {
    const t = piece.trim();
    if (!t) continue;
    const key = t.toUpperCase();
    if (seen.has(key)) continue;      // scanning the same tube twice is a slip
    seen.add(key);
    out.push(t);
  }
  return out;
}

export default function BatchSearchModal({ open, onClose, onApply, initial = '' }) {
  const [text, setText] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Seed from whatever is already in the box, so opening this does not
    // throw away a search somebody had started.
    setText(initial ? parseBatch(initial).join('\n') : '');
    const t = setTimeout(() => ref.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open, initial]);

  if (!open) return null;

  const terms = parseBatch(text);
  const over = terms.length > MAX_TERMS;

  const apply = () => {
    if (!terms.length) return;
    onApply(terms.slice(0, MAX_TERMS).join(SEPARATOR));
    onClose();
  };

  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, zIndex: 4000,
                  background: 'rgba(0,0,0,0.55)', display: 'flex',
                  alignItems: 'flex-start', justifyContent: 'center',
                  paddingTop: '9vh' }}>
      <div onClick={e => e.stopPropagation()}
           style={{ width: 'min(560px, 92vw)', borderRadius: 14,
                    background: 'var(--card-bg, #12151c)',
                    border: '1px solid var(--border-color, #2a3040)',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9,
                      padding: '14px 16px',
                      borderBottom: '1px solid var(--border-color, #2a3040)' }}>
          <ScanLine size={17} color="#6366f1" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>Batch search</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary, #94a3b8)' }}>
              Scan or paste as many as you like — one per line. Enter starts a
              new line; it does not search.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                           color: 'var(--text-secondary, #94a3b8)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 14 }}>
          <textarea
            ref={ref}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              // A wedge scanner ends every barcode with Enter. Submitting on
              // it would end the session after the first tube.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                apply();
              }
            }}
            rows={9}
            placeholder={'ORD000011\nORD000012\nSP000257-1\n…'}
            spellCheck={false}
            style={{ width: '100%', resize: 'vertical', borderRadius: 9,
                     padding: '10px 11px', fontSize: 13,
                     fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                     background: 'rgba(255,255,255,0.04)',
                     border: '1px solid var(--border-color, #2a3040)',
                     color: 'var(--text-primary, #e2e8f0)', outline: 'none' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                        marginTop: 9, fontSize: 11.5,
                        color: 'var(--text-secondary, #94a3b8)' }}>
            <span><strong style={{ color: over ? '#f59e0b' : 'inherit' }}>
              {terms.length}
            </strong> to search{terms.length === 1 ? '' : ''}</span>
            {over && (
              <span style={{ color: '#f59e0b' }}>
                only the first {MAX_TERMS} will be used
              </span>
            )}
            <span style={{ flex: 1 }} />
            {text && (
              <button onClick={() => { setText(''); ref.current?.focus(); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                               color: 'var(--text-secondary, #94a3b8)',
                               display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Trash2 size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8,
                      padding: '12px 14px',
                      borderTop: '1px solid var(--border-color, #2a3040)' }}>
          <button onClick={onClose}
                  style={{ padding: '8px 14px', borderRadius: 8,
                           border: '1px solid var(--border-color, #2a3040)',
                           background: 'transparent', cursor: 'pointer',
                           color: 'var(--text-secondary, #94a3b8)', fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={apply} disabled={!terms.length}
                  style={{ padding: '8px 16px', borderRadius: 8, border: 'none',
                           background: terms.length ? '#6366f1' : 'transparent',
                           color: terms.length ? '#fff' : 'var(--text-secondary, #94a3b8)',
                           cursor: terms.length ? 'pointer' : 'default',
                           fontWeight: 700, fontSize: 13,
                           display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Search size={13} />
            {terms.length ? `Search ${terms.length}` : 'Search'}
          </button>
        </div>
      </div>
    </div>
  );
}
